import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  apiLoadErrorText,
  useGetPayRequestsMetaQuery,
  useListPayRequestsQuery,
  type PayRequestMeta,
  type PayRequestScope,
  type PayRequestStage,
  type PayRequestsFilter,
} from '@shared/api';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { pluralizeRu } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import { IconArrowLeft, IconPlus } from '@shared/ui/icons';
import { STAGES, formatRub } from './model';
import { NewPayRequest } from './NewPayRequest';
import { ClosingDebts } from './ui/ClosingDebts';
import { GroupedRequests, RequestRow } from './ui/RequestRow';
import { daysAgo } from './ui/text';
import {
  ErrorText,
  H1,
  HeaderRow,
  Lead,
  Muted,
  Notice,
  Page,
  RowList,
  Tab,
  TabCount,
  Tabs,
  Toolbar,
  ToolbarItem,
} from './ui/styled';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const DebtList = styled.ul`
  margin: ${({ theme }) => theme.spacing(2)} 0 0;
  padding-left: ${({ theme }) => theme.spacing(5)};

  & a {
    font-weight: 600;
    color: inherit;
  }
`;

/** Содержимое вкладки; пока ищем по новому запросу — прежний список чуть бледнее. */
const Panel = styled.div<{ $stale: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  opacity: ${({ $stale }) => ($stale ? 0.6 : 1)};
  transition: opacity ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
`;

type TabKey = 'mine' | 'department' | 'all' | 'closing';

/** Вкладки, которые пользователю открыты: «Мой отдел» — руководителю отдела,
 *  «Все заявки» — видящим все, «Нет закрывающих» — и тем и другим. */
function tabsFor(meta: PayRequestMeta): TabKey[] {
  const tabs: TabKey[] = ['mine'];
  if (meta.head_of.length > 0) tabs.push('department');
  if (meta.sees_all) tabs.push('all');
  if (meta.sees_all || meta.head_of.length > 0) tabs.push('closing');
  return tabs;
}

const TAB_LABELS: Record<TabKey, string> = {
  mine: 'Мои заявки',
  department: 'Мой отдел',
  all: 'Все заявки',
  closing: 'Нет закрывающих',
};

/** Сколько заявок показываем в плашке «ждём от вас документы». */
const MY_DEBTS_SHOWN = 5;

/** Этап заявки меняет Финансист у себя — вернувшись на страницу, перечитываем. */
const FRESH = { refetchOnMountOrArgChange: 30 } as const;

/**
 * «Заявки на оплату» (`/pay-requests`): свои заявки, заявки отдела и все —
 * по отделам и сотрудникам; «Нет закрывающих» — с кого спросить УПД, акт или
 * счёт-фактуру. `?new=1` — форма новой заявки (назад браузером — к списку),
 * `?tab=` — открытая вкладка.
 */
export function PayRequestsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const metaQ = useGetPayRequestsMetaQuery();
  const meta = metaQ.data;
  const creating = params.get('new') === '1';

  function closeNew() {
    // Форму открыли из списка — «Отмена» возвращает туда же, как и «назад» браузера.
    if (location.key !== 'default') {
      navigate(-1);
      return;
    }
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('new');
        return next;
      },
      { replace: true },
    );
  }

  if (metaQ.isLoading) {
    return (
      <Page>
        <H1>Заявки на оплату</H1>
        <Muted>Загрузка…</Muted>
      </Page>
    );
  }
  if (!meta) {
    return (
      <Page>
        <H1>Заявки на оплату</H1>
        <ErrorText role="alert">
          {apiLoadErrorText(
            metaQ.error,
            'Раздел не загрузился.',
            'Раздел «Заявки на оплату» вам недоступен.',
          )}
        </ErrorText>
        <div>
          <Button type="button" $variant="secondary" onClick={() => void metaQ.refetch()}>
            Повторить
          </Button>
        </div>
      </Page>
    );
  }

  if (creating) {
    return (
      <Page>
        <div>
          <BackLink to="/pay-requests">
            <IconArrowLeft size={14} />
            Заявки на оплату
          </BackLink>
          <H1>Новая заявка на оплату</H1>
        </div>
        <NewPayRequest meta={meta} onCancel={closeNew} />
      </Page>
    );
  }

  return <RequestsList meta={meta} />;
}

function RequestsList({ meta }: { meta: PayRequestMeta }) {
  const [params, setParams] = useSearchParams();
  const tabs = tabsFor(meta);
  const asked = params.get('tab') as TabKey | null;
  const tab: TabKey = asked && tabs.includes(asked) ? asked : meta.sees_all ? 'all' : 'mine';

  const [search, setSearch] = useState('');
  const q = useDebouncedValue(search.trim(), 350);
  const [stage, setStage] = useState<PayRequestStage | ''>('');
  const [serviceId, setServiceId] = useState('');

  const managesClosing = meta.sees_all || meta.head_of.length > 0;
  const closingScope: PayRequestScope = meta.sees_all ? 'all' : 'department';

  // Фильтр по отделу — где отделов в списке больше одного.
  const departmentOptions = useMemo(() => {
    if (tab === 'mine') return [];
    if (meta.sees_all && tab !== 'department') return meta.departments;
    return meta.departments.filter((d) => meta.head_of.includes(d.id));
  }, [meta, tab]);
  const departmentFilter = departmentOptions.length > 1 ? serviceId : '';

  const args: PayRequestsFilter = {
    scope: tab === 'closing' ? closingScope : tab,
    ...(q ? { q } : {}),
    ...(tab !== 'closing' && stage ? { stage } : {}),
    ...(tab === 'closing' ? { closing: 'awaiting' as const } : {}),
    ...(departmentFilter ? { service_id: departmentFilter } : {}),
  };
  const filtered = Boolean(q || (tab !== 'closing' && stage) || departmentFilter);

  // Счётчик на вкладке «Нет закрывающих» и плашка «ждём от вас документы».
  const debtsQ = useListPayRequestsQuery(
    { scope: closingScope, closing: 'awaiting' },
    { ...FRESH, skip: !managesClosing },
  );
  const myDebtsQ = useListPayRequestsQuery({ scope: 'mine', closing: 'awaiting' }, FRESH);
  const myDebts = myDebtsQ.data?.items ?? [];
  const debtsCount = debtsQ.data?.total ?? 0;

  function openTab(key: TabKey) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', key);
        return next;
      },
      { replace: true },
    );
  }

  function openNew() {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('new', '1');
      return next;
    });
  }

  return (
    <Page>
      <HeaderRow>
        <div>
          <H1>Заявки на оплату</H1>
          <Lead>
            Приложите счёт — ОСНОВА сама прочитает реквизиты. Проверьте их и отправьте: заявка сразу
            попадёт к Финансисту в реестр на оплату. После оплаты приложите закрывающие документы:
            УПД, акт или счёт-фактуру.
          </Lead>
        </div>
        <Button type="button" $variant="primary" onClick={openNew}>
          <IconPlus size={16} />
          Новая заявка на оплату
        </Button>
      </HeaderRow>

      {tab === 'mine' && myDebts.length > 0 && (
        <Notice $tone="warning">
          <strong>
            Ждём от вас закрывающие документы по {myDebts.length}{' '}
            {pluralizeRu(myDebts.length, ['заявке', 'заявкам', 'заявкам'])}
          </strong>
          Откройте заявку и приложите УПД, акт или счёт-фактуру — без них бухгалтерия не закроет
          оплату.
          <DebtList>
            {myDebts.slice(0, MY_DEBTS_SHOWN).map((r) => (
              <li key={r.id}>
                <Link to={`/pay-requests/${r.id}`}>{r.counterparty_name}</Link> —{' '}
                {formatRub(r.paid_total)}, {r.request_number ?? 'заявка'}, оплачено{' '}
                {daysAgo(r.days_since_paid)}
              </li>
            ))}
            {myDebts.length > MY_DEBTS_SHOWN && <li>и ещё {myDebts.length - MY_DEBTS_SHOWN}</li>}
          </DebtList>
        </Notice>
      )}

      {tabs.length > 1 && (
        <Tabs aria-label="Какие заявки показать">
          {tabs.map((key) => (
            <Tab
              key={key}
              type="button"
              aria-pressed={tab === key}
              $active={tab === key}
              onClick={() => openTab(key)}
            >
              {TAB_LABELS[key]}
              {key === 'closing' && debtsCount > 0 && (
                <TabCount aria-label={`без закрывающих: ${debtsCount}`}>{debtsCount}</TabCount>
              )}
            </Tab>
          ))}
        </Tabs>
      )}

      <Toolbar>
        <ToolbarItem $grow>
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              tab === 'mine'
                ? 'Поиск: поставщик, ИНН, № счёта, ФП-…'
                : 'Поиск: поставщик, ИНН, № счёта, ФП-…, сотрудник, отдел'
            }
            aria-label="Поиск заявок"
          />
        </ToolbarItem>
        {tab !== 'closing' && (
          <ToolbarItem>
            <Select
              value={stage}
              onChange={(e) => setStage(e.target.value as PayRequestStage | '')}
              aria-label="Где заявка"
            >
              <option value="">Все этапы</option>
              {STAGES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.label}
                </option>
              ))}
            </Select>
          </ToolbarItem>
        )}
        {departmentOptions.length > 1 && (
          <ToolbarItem>
            <Select
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              aria-label="Отдел"
            >
              <option value="">Все отделы</option>
              {departmentOptions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title}
                </option>
              ))}
            </Select>
          </ToolbarItem>
        )}
      </Toolbar>

      {/* Своя подписка на каждую вкладку: при переключении не мелькает список прошлой. */}
      <TabContent key={tab} tab={tab} args={args} filtered={filtered} meta={meta} />
    </Page>
  );
}

interface TabContentProps {
  tab: TabKey;
  args: PayRequestsFilter;
  filtered: boolean;
  meta: PayRequestMeta;
}

function TabContent({ tab, args, filtered, meta }: TabContentProps) {
  const listQ = useListPayRequestsQuery(args, FRESH);
  const order = useMemo(() => meta.departments.map((d) => d.id), [meta]);
  const items = listQ.data?.items ?? [];

  if (listQ.isLoading) return <Muted>Загружаем заявки…</Muted>;
  if (listQ.isError && !listQ.data) {
    return (
      <Notice $tone="danger" role="alert">
        {apiLoadErrorText(listQ.error, 'Заявки не загрузились.', 'Этот список вам недоступен.')}{' '}
        <Button type="button" $variant="ghost" $size="sm" onClick={() => void listQ.refetch()}>
          Повторить
        </Button>
      </Notice>
    );
  }

  let content;
  if (items.length === 0) {
    let empty: string;
    if (filtered) empty = 'Ничего не нашлось — измените поиск или фильтр.';
    else if (tab === 'mine') {
      empty =
        'Заявок пока нет. Нажмите «Новая заявка на оплату» и приложите счёт — ОСНОВА сама прочитает реквизиты, вы проверите их и отправите Финансисту.';
    } else if (tab === 'department') empty = 'В вашем отделе пока нет заявок на оплату.';
    else if (tab === 'all') empty = 'Заявок на оплату пока нет.';
    else empty = 'Все закрывающие на месте — по оплаченным заявкам ждать нечего.';
    content = <Muted>{empty}</Muted>;
  } else if (tab === 'closing') {
    content = <ClosingDebts items={items} order={order} />;
  } else if (tab === 'mine') {
    content = (
      <RowList>
        {items.map((r) => (
          <RequestRow key={r.id} r={r} />
        ))}
      </RowList>
    );
  } else {
    content = <GroupedRequests items={items} order={order} />;
  }

  return (
    <Panel
      $stale={listQ.isFetching && listQ.currentData === undefined}
      aria-busy={listQ.isFetching}
    >
      {content}
    </Panel>
  );
}
